import { useContext, useState, useCallback } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { elasticsearchGet } from '../../services/api';

import { DRUPAL_BASE_URL } from '../../config/drupal.js';
const ENDPOINT = `${DRUPAL_BASE_URL}/api/elasticsearch/analyzable_content`;

const COLUMNS = [
    { key: 'destination_uri_scheme', label: 'URI Scheme' },
    { key: 'destination_url',        label: 'URL de destination' },
    { key: 'http_method',            label: 'Méthode HTTP' },
    { key: 'app_execution_until_stop_duration', label: "Durée d'exécution (ms)" },
    { key: 'web_service_status',     label: 'Statut WS' },
];


/**
 * WsolutionAnalyzableContent — reactive-json element component.
 *
 * YAML usage:
 *   - type: WsolutionAnalyzableContent
 *     wsid: ~~.wsid          # optional pre-filled wsid
 *     pageSize: 20           # optional, default 20
 */
export const WsolutionAnalyzableContent = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext  = useContext(TemplateContext);

    const initialWsid = evaluateTemplateValue({
        valueToEvaluate: props.wsid,
        globalDataContext,
        templateContext,
    }) ?? '';

    const pageSize = Number(
        evaluateTemplateValue({
            valueToEvaluate: props.pageSize,
            globalDataContext,
            templateContext,
        }) ?? 20
    );

    const [wsid, setWsid]       = useState(String(initialWsid));
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState(null);
    const [searched, setSearched] = useState(false);

    const fetchData = useCallback(async () => {
        const trimmed = wsid.trim();
        if (!trimmed) return;

        setLoading(true);
        setError(null);
        setSearched(true);

        try {
            const json = await elasticsearchGet(ENDPOINT, { wsid: trimmed, size: pageSize });
            const hits = json?.hits?.hits ?? [];
            setRows(hits.map((hit) => hit._source ?? {}));
        } catch (err) {
            setError(err.message ?? 'Erreur inconnue');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [wsid, pageSize]);

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchData();
    };

    return (
        <ActionDependant {...props}>
            <div className="p-6 max-w-full">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">
                        WSolution — Analysable Content
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Recherche dans l&apos;index Elasticsearch <code>analyzable_content</code>
                    </p>
                </div>

                {/* Search form */}
                <form onSubmit={handleSubmit} className="flex gap-3 mb-8">
                    <input
                        type="text"
                        value={wsid}
                        onChange={(e) => setWsid(e.target.value)}
                        placeholder="passeplat_wsid…"
                        className="flex-1 max-w-sm border border-gray-300 rounded-lg px-4 py-2 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                        type="submit"
                        disabled={loading || !wsid.trim()}
                        className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg
                                   hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                                   transition-colors"
                    >
                        {loading ? 'Chargement…' : 'Rechercher'}
                    </button>
                </form>

                {/* Error */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                )}

                {/* Results table */}
                {searched && !loading && !error && (
                    <>
                        <p className="text-sm text-gray-500 mb-3">
                            {rows.length === 0
                                ? 'Aucun résultat.'
                                : `${rows.length} résultat${rows.length > 1 ? 's' : ''}`}
                        </p>

                        {rows.length > 0 && (
                            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                                <table className="min-w-full text-sm divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {COLUMNS.map((col) => (
                                                <th
                                                    key={col.key}
                                                    className="px-4 py-3 text-left text-xs font-semibold
                                                               text-gray-600 uppercase tracking-wider whitespace-nowrap"
                                                >
                                                    {col.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {rows.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                {COLUMNS.map((col) => (
                                                    <td
                                                        key={col.key}
                                                        className="px-4 py-3 text-gray-700 max-w-xs truncate"
                                                        title={String(row[col.key] ?? '')}
                                                    >
                                                        {row[col.key] !== undefined && row[col.key] !== null
                                                            ? String(row[col.key])
                                                            : <span className="text-gray-300">—</span>}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* Loading skeleton */}
                {loading && (
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                            <div
                                key={i}
                                className="h-10 bg-gray-100 rounded animate-pulse"
                            />
                        ))}
                    </div>
                )}
            </div>
        </ActionDependant>
    );
};
